import {ActionIcon, AppShell, CloseIcon, Drawer} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {useMediaQuery} from '@mantine/hooks';
import {notifications} from '@mantine/notifications';
import {IconChevronLeft, IconChevronRight} from '@tabler/icons-react';
import React, {useEffect, useMemo} from 'react';
import {GlobalSearchTrigger} from 'src/app/layout/GlobalSearch/GlobalSearchTrigger';
import {SuperAdminGuard} from 'src/app/security/authorization/SuperAdminGuard';
import {
  SidebarGlobalSettingsLinksConfig,
  SidebarInstanceSettingsLinksConfig,
  SidebarUserLinksConfig,
} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {
  setIsSidebarCollapsed,
  setIsSidebarOpen,
} from 'src/redux/slices/interfaceSlice';
import {
  setActiveInstance,
  validateUserSessionSeamless,
} from 'src/redux/slices/sessionSlice';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {AvatarMenu} from './components/AvatarMenu';
import {FloatingSidebarToggle} from './components/FloatingSidebarToggle';
import {Logo} from './components/Logo';
import {SidebarLinks} from './components/SidebarLinks';
import {SidebarScrollArea} from './components/SidebarScrollArea';
import {ProjectScopeSelect} from './components/ProjectScopeSelect';
import {UserInstances} from './components/UserInstances';

import classes from './Sidebar.module.css';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const SidebarWidth = 285;
export const SidebarRailWidth = 64;

export const Sidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery('(max-width: 47.99em)', false, {
    getInitialValueInEffect: false,
  });
  const isDesktop = !isMobile;
  // sm..<lg: the rail is the default so the content column keeps its width;
  // the edge handle still expands it for the rest of the session.
  const prefersRail = useMediaQuery(
    '(min-width: 48em) and (max-width: 74.99em)',
    false,
    {getInitialValueInEffect: false}
  );

  const {instance, instances, isInstanceLoading} = useAppSelector(
    state => state.session
  );
  const {isSidebarOpen, isSidebarCollapsed} = useAppSelector(
    state => state.interface
  );

  const navigate = useAppNavigate();

  // Initialize sidebar state for desktop on mount
  useEffect(() => {
    if (isDesktop) {
      dispatch(setIsSidebarOpen(true));
    }
    if (prefersRail) {
      dispatch(setIsSidebarCollapsed(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrawerClose = () => {
    dispatch(setIsSidebarOpen(false));
  };

  const handleInstanceChange = async (value: string | null): Promise<void> => {
    if (!value) return;
    try {
      await dispatch(setActiveInstance(Number(value))).unwrap();
      await dispatch(validateUserSessionSeamless()).unwrap();
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Unable to switch company',
        message: parseErrorMessage(error),
      });
      return;
    }
    navigate(PathUtility.Dashboard());
  };

  function handleSidebarToggle() {
    dispatch(setIsSidebarOpen(!isSidebarOpen));
  }

  // Explicit values (not a toggle) — the collapse button lives inside the
  // memoized sidebarContents, whose closure can hold a stale isSidebarCollapsed.
  function handleCollapse() {
    dispatch(setIsSidebarCollapsed(true));
  }

  function handleExpand() {
    dispatch(setIsSidebarCollapsed(false));
  }

  const sidebarContents = useMemo(
    () => (
      <div className={classes.sidebarContent}>
        <Box bg="white" p="sm">
          <Logo />
        </Box>

        <ProjectScopeSelect />

        <Box px="sm" py="sm">
          <GlobalSearchTrigger />
        </Box>

        <Divider color="dark.5" mx="sm" mb="xs" />

        <div className={classes.scrollableSection}>
          <SidebarScrollArea>
            <Box px="sm" py="xs">
              <SidebarLinks links={SidebarUserLinksConfig} />

              <Divider color="dark.5" my="xs" />
              <SidebarLinks links={SidebarInstanceSettingsLinksConfig} />

              <SuperAdminGuard>
                <Divider color="dark.5" my="xs" />
                <SidebarLinks links={SidebarGlobalSettingsLinksConfig} />
              </SuperAdminGuard>
            </Box>
          </SidebarScrollArea>
        </div>

        <div className={classes.footer}>
          <Divider color="dark.5" mx="sm" my="xs" />

          <AvatarMenu />

          {instances.length > 1 && (
            <Box mx="sm" pb="xs">
              <UserInstances
                instances={instances}
                selectedInstance={instance?.id}
                onChange={handleInstanceChange}
                isLoading={isInstanceLoading}
              />
            </Box>
          )}
        </div>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instances, instance, isInstanceLoading]
  );

  const railContents = (
    <div className={classes.sidebarContent}>
      <div className={classes.scrollableSection}>
        <SidebarScrollArea scrollbarSize={6}>
          <Box py="xs">
            <SidebarLinks links={SidebarUserLinksConfig} compact />

            <Divider color="dark.5" my="xs" />
            <SidebarLinks links={SidebarInstanceSettingsLinksConfig} compact />

            <SuperAdminGuard>
              <Divider color="dark.5" my="xs" />
              <SidebarLinks links={SidebarGlobalSettingsLinksConfig} compact />
            </SuperAdminGuard>
          </Box>
        </SidebarScrollArea>
      </div>

      <Box pb="sm" style={{display: 'flex', justifyContent: 'center'}}>
        <AvatarMenu compact />
      </Box>
    </div>
  );

  return (
    <>
      <FloatingSidebarToggle onClick={handleSidebarToggle} />

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          opened={isSidebarOpen}
          onClose={handleDrawerClose}
          size={SidebarWidth}
          padding={0}
          withCloseButton={false}
          classNames={{body: classes.drawerBody}}>
          <ActionIcon
            onClick={handleDrawerClose}
            aria-label="Close navigation"
            variant="subtle"
            className={classes.drawerClose}>
            <CloseIcon />
          </ActionIcon>
          {sidebarContents}
        </Drawer>
      )}

      {/* Desktop navbar */}
      <AppShell.Navbar visibleFrom="sm" bg="black" className={classes.navbar}>
        {isSidebarCollapsed ? railContents : sidebarContents}
        <Tooltip
          label={
            isSidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'
          }
          position="right">
          <UnstyledButton
            onClick={isSidebarCollapsed ? handleExpand : handleCollapse}
            aria-label={
              isSidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'
            }
            className={classes.edgeHandle}>
            {isSidebarCollapsed ? (
              <IconChevronRight size={16} stroke={2.5} />
            ) : (
              <IconChevronLeft size={16} stroke={2.5} />
            )}
          </UnstyledButton>
        </Tooltip>
      </AppShell.Navbar>
    </>
  );
};
